import { router } from 'expo-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Platform, Pressable, ScrollView, Switch, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { setAnalyticsConsent } from '@/analytics/analytics';
import { BudgetEstimator, FixedCostDraft } from '@/components/budget-estimator';
import { OnboardingPolicies } from '@/components/onboarding-policies';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { TextField } from '@/components/ui/text-field';
import { ensureNotificationSetup } from '@/notifications/local';
import { useAppMetaStore } from '@/store/app-meta-store';
import { setPendingMarketingConsent, useAuthStore } from '@/store/auth-store';
import { useBudgetStore } from '@/store/budget-store';
import { useFixedCostsStore } from '@/store/fixed-costs-store';
import { useHouseholdStore } from '@/store/household-store';
import { useIncomeStore } from '@/store/income-store';
import { colors, radius, spacing } from '@/theme/tokens';
import type { HouseholdDeclarations } from '@/types/domain';

/**
 * First-run onboarding — INTENT: give a new user a warm, skippable start that
 * (1) explains what the app is for, (2) captures "Twoja sytuacja" so the
 * protection score is meaningful from day one, (3) offers to set a budget, and
 * (4) asks — honestly and opt-in — to help improve the app. No account, no
 * cloud: everything here stays on device until the user chooses otherwise.
 *
 * 1.2.0 additions: a FIRST branching step "Zaczynam / Mam już konto" (restore
 * path goes straight to the login hub — Google/e-mail — then SMS 2FA before the
 * cloud snapshot lands), and an OPTIONAL e-mail + default-unchecked marketing
 * consent on the last step (e-mail = free login identity + renewal reminders).
 *
 * 1.4.0 additions (owner): the situation answers now DO something during
 * onboarding — 'policies' turns each ticked situation into a "zrób zdjęcie
 * polisy" prompt (guest scan -> auto-saved policy we then watch), 'income'
 * asks for the main pay (amount + day) so the month forecast is alive from
 * day one, and the budget calculator's bill rows can become opłaty stałe
 * (amount + "kiedy płacisz?"). All three are skippable like before.
 */

// 'policies' sits at the END (owner decision, mocks onboarding_polisy_wydatki):
// scanning takes a moment, so it must not block the quick early steps.
const STEPS = ['start', 'welcome', 'privacy', 'situation', 'income', 'budget', 'policies', 'improve'] as const;
type Step = (typeof STEPS)[number];

// "Twoje dane zostają u Ciebie" — honest privacy step (owner recommendation:
// tell the user up front we don't collect data; cloud copy is opt-in).
const PRIVACY_POINTS: { icon: keyof typeof Ionicons.glyphMap; titleKey: string; bodyKey: string }[] = [
  { icon: 'phone-portrait-outline', titleKey: 'onboarding.privacyLocalTitle', bodyKey: 'onboarding.privacyLocalBody' },
  { icon: 'eye-off-outline', titleKey: 'onboarding.privacyNoTrackTitle', bodyKey: 'onboarding.privacyNoTrackBody' },
  { icon: 'cloud-outline', titleKey: 'onboarding.privacySyncTitle', bodyKey: 'onboarding.privacySyncBody' },
];

// "Twoja sytuacja" — checkboxes only (owner rule: no X/cross marks for gaps in
// onboarding). Each maps to a household declaration that feeds the protection score.
const SITUATION: { key: keyof HouseholdDeclarations; labelKey: string; icon: keyof typeof Ionicons.glyphMap }[] = [
  { key: 'ownsHome', labelKey: 'onboarding.situationHome', icon: 'home-outline' },
  { key: 'hasCar', labelKey: 'onboarding.situationCar', icon: 'car-outline' },
  { key: 'hasKids', labelKey: 'onboarding.situationKids', icon: 'happy-outline' },
  { key: 'hasMortgage', labelKey: 'onboarding.situationMortgage', icon: 'card-outline' },
  { key: 'runsBusiness', labelKey: 'onboarding.situationBusiness', icon: 'briefcase-outline' },
];

function parseZl(raw: string): number {
  const digits = raw.replace(/[^\d]/g, '');
  return digits ? parseInt(digits, 10) * 100 : 0;
}

export default function OnboardingScreen() {
  const { t } = useTranslation();
  const completeOnboarding = useAppMetaStore((s) => s.completeOnboarding);
  const setUserName = useAppMetaStore((s) => s.setUserName);
  const setDeclarations = useHouseholdStore((s) => s.setDeclarations);
  const household = useHouseholdStore((s) => s.household);
  const setMonthlyBudget = useBudgetStore((s) => s.setMonthlyBudget);
  const addFixedCost = useFixedCostsStore((s) => s.addCost);
  const addIncomeSource = useIncomeStore((s) => s.addSource);

  const [stepIdx, setStepIdx] = useState(0);
  const step: Step = STEPS[stepIdx];

  // Optional first name — asked up front to greet by name (builds trust). Used
  // as-is (mianownik, never declined) so we never invent a wrong vocative.
  const [name, setName] = useState('');

  const [decl, setDecl] = useState<HouseholdDeclarations>({
    ownsHome: false,
    hasMortgage: false,
    hasCar: false,
    hasKids: false,
    runsBusiness: false,
  });
  const [budgetRaw, setBudgetRaw] = useState('');
  const [showCalc, setShowCalc] = useState(false);
  // Bill rows from the calculator the user gave a due day for — created as
  // opłaty stałe in finish() (not at apply-time, so re-opening the calculator
  // never duplicates them: the list is simply replaced on every apply).
  const [pendingFixed, setPendingFixed] = useState<FixedCostDraft[]>([]);
  // 'income' step — the main pay: amount + day drive the adaptive month plan
  // (pierścień prognozy + "czy wpłynęła pensja?"). All fields optional.
  const [incomeName, setIncomeName] = useState('');
  const [incomeAmountRaw, setIncomeAmountRaw] = useState('');
  const [incomeDayRaw, setIncomeDayRaw] = useState('');
  const [improve, setImprove] = useState(true); // opt-in default ON, but shown clearly + toggleable
  // "Mam partnera/partnerkę" — NOT a protection declaration; it only routes the
  // user into the shared-household invite right after onboarding (owner flow).
  const [hasPartner, setHasPartner] = useState(false);
  // 1.2.0: optional e-mail (free login identity — magic link) + marketing
  // consent, DEFAULT UNCHECKED (art. 6 ust. 1 lit. a RODO). Skipping the e-mail
  // = stay a local guest, exactly as before.
  const [email, setEmail] = useState('');
  const [marketingOk, setMarketingOk] = useState(false);
  // "Mam zaproszenie" — the invited partner types the HH code HERE (before,
  // the only path was install -> onboarding -> login -> /household -> "mam
  // kod"). Joining needs an account, so the code is persisted and auto-joined
  // right after the first login (auth-store consumes pendingHouseholdCode).
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState('');

  const startEmailLogin = useAuthStore((s) => s.startEmailLogin);
  const setPendingHouseholdCode = useAppMetaStore((s) => s.setPendingHouseholdCode);

  const toggle = (key: keyof HouseholdDeclarations) => setDecl((d) => ({ ...d, [key]: !d[key] }));

  const emailTrimmed = email.trim();
  const emailValid = emailTrimmed === '' || /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(emailTrimmed);

  // "Mam już konto — przenoszę dane": minimal exit into the login hub. We mark
  // onboarding done so the root redirect stops looping; the cloud snapshot the
  // user is about to restore carries their real setup anyway.
  const restoreExisting = async () => {
    await completeOnboarding();
    router.replace('/auth/login');
  };

  // "Mam zaproszenie": store the code, go log in — the join happens by itself
  // after the session lands, and a bad code just shows a message (never blocks).
  const joinWithInvite = async () => {
    if (!inviteCode.trim()) return;
    await setPendingHouseholdCode(inviteCode);
    await completeOnboarding();
    router.replace('/auth/login');
  };

  const finish = async () => {
    await setUserName(name);
    await setDeclarations(decl);
    const budgetGr = parseZl(budgetRaw);
    if (budgetGr > 0) await setMonthlyBudget(budgetGr);
    // Calculator bill rows with a due day -> real opłaty stałe (reminder +
    // "zapłacone?" flow). addToBudget stays on: confirming a payment writes
    // the expense, exactly like costs added later from the Opłaty tab.
    for (const draft of pendingFixed) {
      await addFixedCost({ name: draft.name, amountGr: draft.amountGr, dueDay: draft.dueDay, addToBudget: true });
    }
    // Main income source (optional): kwota bez dnia = still counted in the
    // forecast; dzień = the app also asks "wpłynęła?" on that day.
    const incomeGr = parseZl(incomeAmountRaw);
    const incomeDay = parseInt(incomeDayRaw, 10);
    if (incomeGr > 0) {
      const ownerId = household?.members.find((m) => m.role === 'OWNER')?.id ?? 'mem-0001';
      await addIncomeSource({
        memberId: ownerId,
        name: incomeName.trim() || t('onboarding.incomeDefaultName'),
        kind: 'ETAT',
        expectedDay: Number.isInteger(incomeDay) && incomeDay >= 1 && incomeDay <= 28 ? incomeDay : null,
        expectedAmountGr: incomeGr,
      });
    }
    // Anything above scheduled a monthly prompt -> ask for the notification
    // permission NOW, in context (no-op on web; policies step asks its own).
    if (pendingFixed.length > 0 || incomeGr > 0) {
      void ensureNotificationSetup().catch(() => undefined);
    }
    await setAnalyticsConsent(improve);
    await completeOnboarding();
    if (emailTrimmed && emailValid) {
      // E-mail given → kick off the magic-link login; the consent is saved
      // right after the session lands (setPendingMarketingConsent).
      setPendingMarketingConsent(marketingOk);
      const result = await startEmailLogin(emailTrimmed);
      if (result.ok) {
        router.replace('/auth/email-sent');
        return;
      }
      // Mail failed (rate limit/offline): fall through to the normal start —
      // the user can log in later from Więcej, nothing is lost.
    }
    if (hasPartner) router.replace({ pathname: '/household-invite', params: { from: 'onboarding' } });
    else router.replace('/(tabs)');
  };

  const next = () => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  const back = () => setStepIdx((i) => Math.max(0, i - 1));

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        {/* Progress dots */}
        <View style={{ flexDirection: 'row', gap: spacing.xs, justifyContent: 'center', paddingTop: spacing.md }}>
          {STEPS.map((s, i) => (
            <View
              key={s}
              style={{
                width: i === stepIdx ? 24 : 8,
                height: 8,
                borderRadius: radius.full,
                backgroundColor: i === stepIdx ? colors.primary : colors.outlineVariant,
              }}
            />
          ))}
        </View>

        <ScrollView
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ padding: spacing.screen, gap: spacing.lg, paddingBottom: spacing.xl, flexGrow: 1 }}
        >
          {step === 'start' && (
            <View style={{ flex: 1, justifyContent: 'center', gap: spacing.md }}>
              <AppText variant="headlineLg" style={{ textAlign: 'center' }}>
                {t('onboarding.startTitle')}
              </AppText>
              <Pressable
                accessibilityRole="button"
                onPress={next}
                style={{
                  backgroundColor: colors.primaryTile,
                  borderRadius: radius.card,
                  padding: spacing.md,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: spacing.md,
                  minHeight: 88,
                }}
              >
                <Ionicons name="sparkles-outline" size={32} color={colors.primary} />
                <View style={{ flex: 1, gap: 2 }}>
                  <AppText variant="headlineSm" color={colors.navy}>
                    {t('onboarding.startNew')}
                  </AppText>
                  <AppText variant="bodyMd" color={colors.textSecondary}>
                    {t('onboarding.startNewHint')}
                  </AppText>
                </View>
                <Ionicons name="chevron-forward" size={22} color={colors.primary} />
              </Pressable>
              <Pressable
                accessibilityRole="button"
                onPress={restoreExisting}
                style={{
                  backgroundColor: colors.primaryTile,
                  borderRadius: radius.card,
                  padding: spacing.md,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: spacing.md,
                  minHeight: 88,
                }}
              >
                <Ionicons name="cloud-download-outline" size={32} color={colors.primary} />
                <View style={{ flex: 1, gap: 2 }}>
                  <AppText variant="headlineSm" color={colors.navy}>
                    {t('onboarding.startRestore')}
                  </AppText>
                  <AppText variant="bodyMd" color={colors.textSecondary}>
                    {t('onboarding.startRestoreHint')}
                  </AppText>
                </View>
                <Ionicons name="chevron-forward" size={22} color={colors.primary} />
              </Pressable>
              {/* Napper-style invite entry: "X zaprasza Cię..." arrives with a
                  code — the invited person can act on it from the FIRST screen. */}
              <Pressable
                accessibilityRole="button"
                onPress={() => setInviteOpen((v) => !v)}
                style={{
                  backgroundColor: colors.primaryTile,
                  borderRadius: radius.card,
                  padding: spacing.md,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: spacing.md,
                  minHeight: 88,
                }}
              >
                <Ionicons name="people-outline" size={32} color={colors.primary} />
                <View style={{ flex: 1, gap: 2 }}>
                  <AppText variant="headlineSm" color={colors.navy}>
                    {t('onboarding.startInvite')}
                  </AppText>
                  <AppText variant="bodyMd" color={colors.textSecondary}>
                    {t('onboarding.startInviteHint')}
                  </AppText>
                </View>
                <Ionicons name={inviteOpen ? 'chevron-down' : 'chevron-forward'} size={22} color={colors.primary} />
              </Pressable>
              {inviteOpen && (
                <View style={{ gap: spacing.sm }}>
                  <TextField
                    label={t('onboarding.inviteCodeLabel')}
                    placeholder="HH-XXX-XXX"
                    autoCapitalize="characters"
                    value={inviteCode}
                    onChangeText={setInviteCode}
                  />
                  <PrimaryButton
                    label={t('onboarding.inviteCodeCta')}
                    disabled={inviteCode.trim().length < 6}
                    onPress={() => void joinWithInvite()}
                  />
                </View>
              )}
            </View>
          )}

          {step === 'welcome' && (
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', gap: spacing.md }}>
              <View
                style={{
                  width: 96,
                  height: 96,
                  borderRadius: radius.full,
                  backgroundColor: colors.primaryTile,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Ionicons name="shield-checkmark-outline" size={52} color={colors.navy} />
              </View>
              <AppText variant="headlineLg" style={{ textAlign: 'center' }}>
                {t('onboarding.welcomeTitle')}
              </AppText>
              <AppText variant="bodyLg" style={{ textAlign: 'center' }}>
                {t('onboarding.welcomeBody')}
              </AppText>
              <View style={{ gap: spacing.sm, alignSelf: 'stretch', marginTop: spacing.sm }}>
                {['budget', 'policies', 'goals', 'reminders'].map((k) => (
                  <View key={k} style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                    <Ionicons name="checkmark-circle" size={22} color={colors.primary} />
                    <AppText variant="bodyLg">{t(`onboarding.welcomeFeature.${k}`)}</AppText>
                  </View>
                ))}
              </View>
              {/* Optional first name — greeting uses it as-is, never declined. */}
              <View style={{ alignSelf: 'stretch', marginTop: spacing.sm }}>
                <TextField
                  label={t('onboarding.nameLabel')}
                  placeholder={t('onboarding.namePlaceholder')}
                  value={name}
                  onChangeText={setName}
                  autoCapitalize="words"
                />
              </View>
            </View>
          )}

          {step === 'privacy' && (
            <View style={{ gap: spacing.md }}>
              <AppText variant="headlineMd">{t('onboarding.privacyTitle')}</AppText>
              <AppText variant="bodyMd">{t('onboarding.privacySubtitle')}</AppText>
              <Card style={{ padding: 0 }}>
                {PRIVACY_POINTS.map((item, idx) => (
                  <View
                    key={item.titleKey}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      gap: spacing.sm,
                      padding: spacing.md,
                      borderTopWidth: idx === 0 ? 0 : 1,
                      borderTopColor: colors.divider,
                    }}
                  >
                    <Ionicons name={item.icon} size={24} color={colors.primary} />
                    <View style={{ flex: 1, gap: 2 }}>
                      <AppText variant="labelLg">{t(item.titleKey)}</AppText>
                      <AppText variant="bodyMd" color={colors.textSecondary}>
                        {t(item.bodyKey)}
                      </AppText>
                    </View>
                  </View>
                ))}
              </Card>
            </View>
          )}

          {step === 'situation' && (
            <View style={{ gap: spacing.md }}>
              <AppText variant="headlineMd">{t('onboarding.situationTitle')}</AppText>
              <AppText variant="bodyMd">{t('onboarding.situationSubtitle')}</AppText>
              <Card style={{ padding: 0 }}>
                {/* Owner: tester ticked "dom" although he lives with his mum. The
                    labels now say OWNERSHIP, and a note makes clear it's optional. */}
                {SITUATION.map((item, idx) => {
                  const checked = decl[item.key];
                  return (
                    <Pressable
                      key={item.key}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked }}
                      onPress={() => toggle(item.key)}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: spacing.sm,
                        padding: spacing.md,
                        minHeight: 60,
                        borderTopWidth: idx === 0 ? 0 : 1,
                        borderTopColor: colors.divider,
                      }}
                    >
                      <Ionicons name={item.icon} size={24} color={colors.navy} />
                      <AppText variant="bodyLg" style={{ flex: 1 }}>
                        {t(item.labelKey)}
                      </AppText>
                      {/* Checkbox: filled square when on, empty outline when off —
                          NEVER an X (owner: onboarding shows no "gaps"). */}
                      <View
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: radius.chip,
                          borderWidth: checked ? 0 : 2,
                          borderColor: colors.outlineVariant,
                          backgroundColor: checked ? colors.primary : 'transparent',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {checked && <Ionicons name="checkmark" size={20} color="#FFFFFF" />}
                      </View>
                    </Pressable>
                  );
                })}
                {/* Partner row — routes into the shared-household invite after
                    onboarding; not a protection declaration. */}
                <Pressable
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: hasPartner }}
                  onPress={() => setHasPartner((v) => !v)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: spacing.sm,
                    padding: spacing.md,
                    minHeight: 60,
                    borderTopWidth: 1,
                    borderTopColor: colors.divider,
                  }}
                >
                  <Ionicons name="people-outline" size={24} color={colors.navy} />
                  <AppText variant="bodyLg" style={{ flex: 1 }}>
                    {t('onboarding.situationPartner')}
                  </AppText>
                  <View
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: radius.chip,
                      borderWidth: hasPartner ? 0 : 2,
                      borderColor: colors.outlineVariant,
                      backgroundColor: hasPartner ? colors.primary : 'transparent',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {hasPartner && <Ionicons name="checkmark" size={20} color="#FFFFFF" />}
                  </View>
                </Pressable>
              </Card>
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xs }}>
                <Ionicons name="information-circle-outline" size={16} color={colors.outline} style={{ marginTop: 2 }} />
                <AppText variant="labelMd" color={colors.outline} style={{ flex: 1 }}>
                  {t('onboarding.situationNote')}
                </AppText>
              </View>
            </View>
          )}

          {step === 'policies' && <OnboardingPolicies decl={decl} hasPartner={hasPartner} />}

          {step === 'income' && (
            <View style={{ gap: spacing.md }}>
              <AppText variant="headlineMd">{t('onboarding.incomeTitle')}</AppText>
              <AppText variant="bodyMd">{t('onboarding.incomeSubtitle')}</AppText>
              {/* Amount first — it's the field that matters; the president skipped
                  the step because the optional name field led (2026-07-18). */}
              <TextField
                label={t('onboarding.incomeAmountLabel')}
                placeholder="np. 5 000"
                keyboardType="numeric"
                value={incomeAmountRaw}
                onChangeText={setIncomeAmountRaw}
              />
              <TextField
                label={t('onboarding.incomeNameLabel')}
                placeholder={t('onboarding.incomeNamePlaceholder')}
                value={incomeName}
                onChangeText={setIncomeName}
              />
              <TextField
                label={t('onboarding.incomeDayLabel')}
                placeholder="np. 10"
                keyboardType="numeric"
                value={incomeDayRaw}
                onChangeText={(v) => setIncomeDayRaw(v.replace(/[^\d]/g, '').slice(0, 2))}
              />
              <AppText variant="labelMd" color={colors.outline}>
                {t('onboarding.incomeHint')}
              </AppText>
            </View>
          )}

          {step === 'budget' && (
            <View style={{ gap: spacing.md }}>
              <AppText variant="headlineMd">{t('onboarding.budgetTitle')}</AppText>
              <AppText variant="bodyMd">{t('onboarding.budgetSubtitle')}</AppText>
              <TextField
                label={t('onboarding.budgetLabel')}
                placeholder="np. 4 000"
                keyboardType="numeric"
                value={budgetRaw}
                onChangeText={setBudgetRaw}
              />
              <AppText variant="labelMd" color={colors.outline}>
                {t('onboarding.budgetHint')}
              </AppText>
              {/* Guided calculator — same helper as the Budżet tab. */}
              <Pressable
                accessibilityRole="button"
                onPress={() => setShowCalc((v) => !v)}
                style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, minHeight: 40 }}
              >
                <Ionicons name={showCalc ? 'chevron-up' : 'calculator-outline'} size={18} color={colors.primary} />
                <AppText variant="labelLg" color={colors.primary}>
                  {t('summary.budgetHelpCta')}
                </AppText>
              </Pressable>
              {showCalc && (
                <BudgetEstimator
                  collectFixedDays
                  onApply={(totalGr, fixedDrafts) => {
                    setBudgetRaw(String(Math.round(totalGr / 100)));
                    // Replace (never append) — a second apply must not duplicate.
                    setPendingFixed(fixedDrafts ?? []);
                    setShowCalc(false);
                  }}
                />
              )}
              {pendingFixed.length > 0 && (
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xs }}>
                  <Ionicons name="checkmark-circle" size={16} color={colors.primary} style={{ marginTop: 2 }} />
                  <AppText variant="labelMd" color={colors.navy} style={{ flex: 1 }}>
                    {t('onboarding.budgetFixedInfo', {
                      list: pendingFixed.map((f) => `${f.name} (${f.dueDay}.)`).join(', '),
                    })}
                  </AppText>
                </View>
              )}
            </View>
          )}

          {step === 'improve' && (
            <View style={{ gap: spacing.md }}>
              <AppText variant="headlineMd">{t('onboarding.improveTitle')}</AppText>
              <Card style={{ gap: spacing.sm }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                  <Ionicons name="heart-outline" size={24} color={colors.primary} />
                  <AppText variant="bodyLg" style={{ flex: 1 }}>
                    {t('onboarding.improveToggle')}
                  </AppText>
                  <Switch
                    value={improve}
                    onValueChange={setImprove}
                    trackColor={{ false: colors.outlineVariant, true: colors.primary }}
                    thumbColor="#FFFFFF"
                    ios_backgroundColor={colors.outlineVariant}
                  />
                </View>
                <AppText variant="bodyMd" color={colors.outline}>
                  {t('onboarding.improveHint')}
                </AppText>
              </Card>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                <Ionicons name="lock-closed-outline" size={16} color={colors.navy} />
                <AppText variant="labelMd" color={colors.navy} style={{ flex: 1 }}>
                  {t('onboarding.improvePrivacy')}
                </AppText>
              </View>

              {/* 1.2.0: optional e-mail = free login (magic link) + data safety.
                  Marketing consent DEFAULT-UNCHECKED, phrased as the user's
                  benefit (renewal reminders), never as "we want to sell". */}
              <AppText variant="headlineMd" style={{ marginTop: spacing.md }}>
                {t('onboarding.contactTitle')}
              </AppText>
              <Card style={{ gap: spacing.sm }}>
                <TextField
                  label={t('onboarding.contactEmailLabel')}
                  placeholder="twoj@email.pl"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  value={email}
                  onChangeText={setEmail}
                />
                <AppText variant="labelMd" color={colors.outline}>
                  {t('onboarding.contactEmailHint')}
                </AppText>
                <Pressable
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: marketingOk }}
                  onPress={() => setMarketingOk((v) => !v)}
                  style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm, minHeight: 44 }}
                >
                  <View
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: radius.chip,
                      borderWidth: marketingOk ? 0 : 2,
                      borderColor: colors.outlineVariant,
                      backgroundColor: marketingOk ? colors.primary : 'transparent',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: 2,
                    }}
                  >
                    {marketingOk && <Ionicons name="checkmark" size={18} color="#FFFFFF" />}
                  </View>
                  <AppText variant="bodyMd" style={{ flex: 1 }}>
                    {t('onboarding.marketingConsent')}
                  </AppText>
                </Pressable>
              </Card>
              {!emailValid && (
                <AppText variant="labelMd" color={colors.warning}>
                  {t('onboarding.contactEmailInvalid')}
                </AppText>
              )}
            </View>
          )}
        </ScrollView>

        {/* Footer nav — reserves the bottom bar; primary button always solid.
            The 'start' step navigates via its two big cards, so no button. */}
        <View style={{ padding: spacing.screen, gap: spacing.sm }}>
          {step === 'improve' ? (
            <PrimaryButton label={t('onboarding.finish')} onPress={finish} disabled={!emailValid} />
          ) : step !== 'start' ? (
            <PrimaryButton label={t('onboarding.next')} onPress={next} />
          ) : null}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            {stepIdx > 0 ? (
              <Pressable onPress={back} accessibilityRole="button" style={{ minHeight: 44, justifyContent: 'center' }}>
                <AppText variant="labelLg" color={colors.navy}>
                  {t('onboarding.back')}
                </AppText>
              </Pressable>
            ) : (
              <View />
            )}
            {/* Optional steps can be skipped straight to the end */}
            {(step === 'situation' || step === 'policies' || step === 'income' || step === 'budget') && (
              <Pressable
                onPress={() => setStepIdx(STEPS.length - 1)}
                accessibilityRole="button"
                style={{ minHeight: 44, justifyContent: 'center' }}
              >
                <AppText variant="labelLg" color={colors.outline}>
                  {t('onboarding.skip')}
                </AppText>
              </Pressable>
            )}
          </View>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
